import * as React from 'react';
import { cn } from '@/lib/utils';

const GuideBlock = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-guide-block',
      // Estilos base
      'flex max-w-full flex-col gap-4',
      // Estilos navegação
      '[&_.c-guide-block-nav]:shrink-0 [&_.c-guide-block-nav]:lg:w-[236px]',
      // Estilos artigo
      '[&_.c-guide-block-article]:grow',
      className
    )}
    {...props}
  />
));
GuideBlock.displayName = 'GuideBlock';

const GuideBlockHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-guide-block-header',
      // Estilos base
      'flex flex-wrap items-center justify-between gap-4',
      className
    )}
    {...props}
  />
));
GuideBlockHeader.displayName = 'GuideBlockHeader';

const GuideBlockHeaderContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-guide-block-header-content',
      // Estilos base
      '',
      className
    )}
    {...props}
  />
));
GuideBlockHeaderContent.displayName = 'GuideBlockHeaderContent';

const GuideBlockTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn(
      'c-guide-block-title',
      // Estilos base — no mobile o texto-2xl ficava grande demais pra largura da tela
      'mb-0 text-lg font-bold leading-snug text-primary-dark',
      // Estilos responsivos
      'lg:text-2xl lg:leading-normal',
      className
    )}
    {...props}
  />
));
GuideBlockTitle.displayName = 'GuideBlockTitle';

const GuideBlockBtn = React.forwardRef(({ className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'c-guide-block-btn',
      // Estilos base
      'inline-flex items-center gap-2',
      'cursor-pointer text-sm font-light text-primary',
      // Estilos ícone
      '[&>svg]:w-6 [&>svg]:shrink-0 [&>svg]:stroke-[1px]',
      className
    )}
    {...props}
  />
));
GuideBlockBtn.displayName = 'GuideBlockBtn';

const GuideBlockContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-guide-block-content',
      // Estilos base — pb extra no mobile pra a barra de steps não ficar colada no bottom nav
      'flex flex-col gap-[42px] pb-8',
      // Estilos responsivos
      'lg:flex-row lg:gap-[84px] lg:pb-0',
      className
    )}
    {...props}
  />
));
GuideBlockContent.displayName = 'GuideBlockContent';

const GuideBlockPlayer = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-guide-block-player',
      // Estilos base
      '[&_iframe]:aspect-[16/9]',
      className
    )}
    {...props}
  />
));
GuideBlockPlayer.displayName = 'GuideBlockPlayer';

const GuideBlockNav = React.forwardRef(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn(
      'c-guide-block-nav',
      // Estilos base — no mobile vira uma barra horizontal de steps
      'order-2 flex flex-row items-start gap-0',
      // Estilos responsivos — no lg+ volta pra lista vertical de cards.
      // w-[236px] + shrink-0: a coluna precisa de largura FIXA — sem isso o ul dimensiona
      // pelo card mais largo (min-width:auto impede o flex de encolher abaixo do conteúdo)
      // e os cards com título longo sobrepõem o artigo ao lado; com a largura travada,
      // o lg:truncate do título finalmente tem um limite pra agir.
      'lg:order-1 lg:w-[236px] lg:shrink-0 lg:flex-col lg:gap-3',
      className
    )}
    {...props}
  />
));
GuideBlockNav.displayName = 'GuideBlockNav';

const GuideBlockNavItem = React.forwardRef(({ className, isActive, isSuccess, ...props }, ref) => (
  <li
    ref={ref}
    className={cn(
      'c-guide-block-nav-item',
      // Estilos base — step compacto (ícone + linha conectora), sem card
      'relative flex flex-1 cursor-default flex-col items-center gap-1',
      // Linha conectora entre os steps (mobile only)
      'max-lg:after:absolute max-lg:after:left-1/2 max-lg:after:top-3.5 max-lg:after:-z-10 max-lg:after:h-0.5 max-lg:after:w-full max-lg:after:bg-neutral-100 max-lg:last:after:hidden',
      // Estilos responsivos — no lg+ volta a ser o card horizontal original. lg:flex-none
      // desfaz o flex-1 (pensado só pra barra de steps do mobile, onde cada step some espaço
      // horizontal igual) — sem isso, em flex-col ele estica a ALTURA de cada card igualmente
      // pra preencher a coluna toda, inflando os cards bem além do conteúdo.
      // w-full: trava o card na largura da coluna (236px) — sem isso o card com título
      // longo dimensiona pelo conteúdo e vaza por cima do artigo ao lado
      'lg:w-full lg:max-w-full lg:flex-none lg:flex-row lg:items-center lg:gap-2 lg:p-4 lg:px-3',
      'lg:rounded-2xl lg:border lg:border-solid lg:border-neutral-100 lg:bg-white lg:bg-clip-padding',
      'lg:shadow-card',
      // Variantes (só no card do lg+; no mobile o estado já aparece no ícone)
      isActive && 'lg:bg-bright',
      isSuccess && 'lg:bg-success',
      className
    )}
    {...props}
  />
));
GuideBlockNavItem.displayName = 'GuideBlockNavItem';

const GuideBlockNavIcon = React.forwardRef(({ className, isActive, isSuccess, done, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-guide-block-nav-icon',
      // Estilos base — no mobile é um numeral em círculo (sem cadeado, não é punitivo)
      'relative inline-flex h-7 w-7 shrink-0 items-center justify-center text-[11px] font-bold',
      'rounded-full border border-solid border-neutral-100 bg-white text-neutral-300',
      // Estilos ícone
      '[&>img]:w-4 [&>svg]:w-3.5',
      // Estilos responsivos — no lg+ (card horizontal) volta ao badge original
      'lg:h-6 lg:w-6 lg:border-0 lg:bg-bright lg:text-primary lg:[&>svg]:w-4',
      isActive && 'lg:bg-white',
      // Variantes
      isSuccess && 'border-primary bg-primary text-white lg:bg-white lg:text-success',
      done && 'border-success bg-success text-white lg:bg-white',
      className
    )}
    {...props}
  />
));
GuideBlockNavIcon.displayName = 'GuideBlockNavIcon';

const GuideBlockNavTitle = React.forwardRef(({ className, isSuccess, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'c-guide-block-nav-title',
      // Estilos base — escondido no mobile (o título da seção ativa já aparece no artigo)
      'hidden',
      'text-xs font-bold text-primary-dark',
      // Estilos responsivos
      'lg:mb-0 lg:block lg:grow lg:truncate',
      isSuccess && 'lg:text-white',
      className
    )}
    {...props}
  />
));
GuideBlockNavTitle.displayName = 'GuideBlockNavTitle';

const GuideBlockNavCheck = React.forwardRef(({ className, isSuccess, checked, ...props }, ref) => {
  return (
    <div className={cn('relative hidden items-center justify-center lg:inline-flex', className)}>
      <input
        ref={ref}
        type="checkbox"
        className="sr-only"
        checked={checked}
        {...props}
      />

      <span
        className={cn(
          'c-study-guide-card-check',
          // Estilos base do checkbox visual
          'inline-flex items-center justify-center',
          'm-0 size-4 shrink-0 appearance-none',
          'rounded-full border border-solid border-neutral-light bg-white',
          'transition-all duration-200 ease-in-out',
          'cursor-pointer bg-center outline-none',
          // Estilos de estado
          checked && 'border-success bg-success',
          isSuccess && 'border-white bg-white'
        )}
      >
        {checked && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`h-3 w-3 ${isSuccess ? 'stroke-success' : 'stroke-white'}`}
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        )}
      </span>
    </div>
  );
});
GuideBlockNavCheck.displayName = 'GuideBlockNavCheck';

const GuideBlockArticle = React.forwardRef(({ className, ...props }, ref) => (
  <article
    ref={ref}
    className={cn(
      'c-guide-block-article',
      // Estilos base — espelha os cards da trilha (rounded-2xl + shadow-card + borda neutra)
      // order-1: no mobile o conteúdo vem antes da navegação (steps ficam depois)
      'order-1 flex flex-col gap-5 p-8',
      'lg:order-2',
      'rounded-2xl border border-solid border-neutral-100 bg-white',
      'shadow-card',
      className
    )}
    {...props}
  />
));
GuideBlockArticle.displayName = 'GuideBlockArticle';

const GuideBlockArticleHat = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'c-guide-block-article-hat',
      // Estilos base
      'mb-0',
      'text-sm font-bold text-neutral',
      className
    )}
    {...props}
  />
));
GuideBlockArticleHat.displayName = 'GuideBlockArticleHat';

const GuideBlockArticleTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h1
    ref={ref}
    className={cn(
      'c-guide-block-article-title',
      // Estilos base
      'mb-0',
      'text-2xl font-bold text-primary-dark',
      className
    )}
    {...props}
  />
));
GuideBlockArticleTitle.displayName = 'GuideBlockArticleTitle';

const GuideBlockArticleHeader = React.forwardRef(({ className, ...props }, ref) => (
  <header
    ref={ref}
    className={cn(
      'c-guide-block-article__title',
      // Estilos base
      '',
      className
    )}
    {...props}
  />
));
GuideBlockArticleHeader.displayName = 'GuideBlockArticleHeader';

const GuideBlockArticleContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-guide-block-article__content',
      // Estilos para todos os filhos diretos
      '[&>*]:text-neutral-darker',
      // Estilos para p e ul filhos diretos
      '[&>p]:mb-8',
      '[&>ul]:mb-8',
      // Estilos para todas as imagens internas
      '[&_img]:w-[512px]',
      '[&_img]:rounded-[10px]',
      // Estilos para todas as listas internas
      '[&_ul]:list-disc',
      '[&_ul]:pl-5',
      className
    )}
    {...props}
  />
));
GuideBlockArticleContent.displayName = 'GuideBlockArticleContent';

const GuideBlockArticleFooter = React.forwardRef(({ className, ...props }, ref) => (
  <footer
    ref={ref}
    className={cn(
      'c-guide-block-article-footer',
      // No mobile fica no fluxo normal (sem flutuar por cima do conteúdo/bottom nav).
      'mt-2 flex justify-center',
      // No lg+ volta a ser o botão sólido flutuante (espelha o CTA da trilha)
      'lg:fixed lg:bottom-8 lg:right-8 lg:z-10 lg:mt-0 lg:block',
      className
    )}
    {...props}
  />
));
GuideBlockArticleFooter.displayName = 'GuideBlockArticleFooter';

export {
  GuideBlock,
  GuideBlockHeader,
  GuideBlockHeaderContent,
  GuideBlockTitle,
  GuideBlockBtn,
  GuideBlockContent,
  GuideBlockPlayer,
  GuideBlockNav,
  GuideBlockNavItem,
  GuideBlockNavIcon,
  GuideBlockNavTitle,
  GuideBlockNavCheck,
  GuideBlockArticle,
  GuideBlockArticleHat,
  GuideBlockArticleTitle,
  GuideBlockArticleHeader,
  GuideBlockArticleContent,
  GuideBlockArticleFooter
};
